import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth.js";

const LandingPage = lazy(() => import("./pages/Landing.js").then((m) => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import("./routes/login.js").then((m) => ({ default: m.LoginPage })));
const PrivacyNoticePage = lazy(() =>
  import("./routes/privacy.js").then((m) => ({ default: m.PrivacyNoticePage })),
);
const TermsPage = lazy(() => import("./routes/terms.js").then((m) => ({ default: m.TermsPage })));
const CardRecipientScreen = lazy(() =>
  import("./routes/card-recipient.js").then((m) => ({ default: m.CardRecipientScreen })),
);
const HomeScreen = lazy(() => import("./routes/home.js").then((m) => ({ default: m.HomeScreen })));
const NewEventScreen = lazy(() =>
  import("./routes/event-new.js").then((m) => ({ default: m.NewEventScreen })),
);
const AddGiftsScreen = lazy(() =>
  import("./routes/event-detail.js").then((m) => ({ default: m.AddGiftsScreen })),
);
const EventContextChatScreen = lazy(() =>
  import("./routes/event-context-chat.js").then((m) => ({ default: m.EventContextChatScreen })),
);
const ChatScreen = lazy(() =>
  import("./routes/gift-detail.js").then((m) => ({ default: m.ChatScreen })),
);
const LettersScreen = lazy(() =>
  import("./routes/letters.js").then((m) => ({ default: m.LettersScreen })),
);
const ContactsScreen = lazy(() =>
  import("./routes/contacts.js").then((m) => ({ default: m.ContactsScreen })),
);
const CorrespondenceScreen = lazy(() =>
  import("./routes/correspondence.js").then((m) => ({ default: m.CorrespondenceScreen })),
);
const CorrespondenceDetailScreen = lazy(() =>
  import("./routes/correspondence.js").then((m) => ({ default: m.CorrespondenceDetailScreen })),
);
const LetterPreviewScreen = lazy(() =>
  import("./routes/letter-preview.js").then((m) => ({ default: m.LetterPreviewScreen })),
);
const SendScreen = lazy(() => import("./routes/send.js").then((m) => ({ default: m.SendScreen })));
const PricingScreen = lazy(() =>
  import("./routes/pricing.js").then((m) => ({ default: m.PricingScreen })),
);
const CheckoutReturnScreen = lazy(() =>
  import("./routes/checkout-return.js").then((m) => ({ default: m.CheckoutReturnScreen })),
);
const SettingsScreen = lazy(() =>
  import("./routes/settings.js").then((m) => ({ default: m.SettingsScreen })),
);
const OnboardingScreen = lazy(() =>
  import("./routes/onboarding.js").then((m) => ({ default: m.OnboardingScreen })),
);
const AdminScreen = lazy(() =>
  import("./routes/admin.js").then((m) => ({ default: m.AdminScreen })),
);
const EmployeesScreen = lazy(() =>
  import("./routes/employees.js").then((m) => ({ default: m.EmployeesScreen })),
);
const EmployeeDetailScreen = lazy(() =>
  import("./routes/employee-detail.js").then((m) => ({ default: m.EmployeeDetailScreen })),
);

const LoadingScreen = () => (
  <div className="container">
    <p className="muted">Loading…</p>
  </div>
);

export const App = () => {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyNoticePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/c/:token" element={<CardRecipientScreen />} />
          <Route path="*" element={<LoadingScreen />} />
        </Routes>
      </Suspense>
    );
  }

  if (status === "signed-out") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyNoticePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/c/:token" element={<CardRecipientScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/privacy" element={<PrivacyNoticePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/c/:token" element={<CardRecipientScreen />} />
        <Route path="/events/new" element={<NewEventScreen />} />
        <Route path="/events/:eventId" element={<AddGiftsScreen />} />
        <Route path="/events/:eventId/context" element={<EventContextChatScreen />} />
        <Route path="/events/:eventId/letters" element={<LettersScreen />} />
        <Route path="/contacts" element={<ContactsScreen />} />
        <Route path="/correspondence" element={<CorrespondenceScreen />} />
        <Route path="/correspondence/:deliveryId" element={<CorrespondenceDetailScreen />} />
        <Route path="/gifts/:giftId" element={<ChatScreen />} />
        <Route path="/letters/:letterId/preview" element={<LetterPreviewScreen />} />
        <Route path="/letters/:letterId/send" element={<SendScreen />} />
        <Route path="/pricing" element={<PricingScreen />} />
        <Route path="/checkout/return" element={<CheckoutReturnScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/admin" element={<AdminScreen />} />
        <Route path="/employees" element={<EmployeesScreen />} />
        <Route path="/employees/:employeeId" element={<EmployeeDetailScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};
